/**
 * 无依赖白名单 HTML 净化器（防存储型 / 反射型 XSS）。
 *
 * 背景：后台多处用 v-html 渲染后端/玩家来源内容（投注号码、betTxt、公告、活动规则等）。
 * 这些内容里既有前端/规则引擎合法产出的展示标记（如中奖标红 <span class="fc-red">、<br>），
 * 又可能夹带玩家注入的 <img onerror>、<script> 等可执行片段 → 存储型 XSS（可盗管理员 token）。
 * 因 convert 规则会合法输出 span/br，故不能整体转义或退化为纯文本，必须做“白名单净化”。
 *
 * 实现要点：
 *  - 用 DOMParser('text/html') 把脏 HTML 解析为**惰性文档**——不执行脚本、不发起资源请求；
 *  - 深度遍历，白名单外的元素连同子树整体删除，绝不 unwrap（避免被移出的子节点漏过净化）；
 *  - 白名单内的元素逐属性清洗：删 on*、删非白名单属性、扫 style 危险片段、校验 href/src scheme；
 *  - 非浏览器环境（SSR/单测）兜底退化为纯文本实体转义。
 *
 * 两套预设：
 *  - basic（默认）：投注详情等，仅内联展示标记 span/br/div/b/i/font + class/style；
 *  - rich：公告/活动规则等管理员富文本，额外放行 a[href]/img[src]/标题/列表/表格。
 */

// 仅内联展示标记
const BASIC_TAGS = [
  'SPAN', 'BR', 'DIV', 'P', 'B', 'I', 'EM', 'STRONG', 'U', 'FONT', 'SMALL', 'SUB', 'SUP'
];
// 富文本：在 basic 基础上放行链接/图片/标题/列表/表格等
const RICH_TAGS = BASIC_TAGS.concat([
  'A', 'IMG', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
  'BLOCKQUOTE', 'PRE', 'CODE', 'HR', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'LABEL'
]);

const BASIC_ATTRS = ['class', 'style', 'align', 'color', 'width', 'height'];
const RICH_ATTRS = BASIC_ATTRS.concat(['href', 'src', 'alt', 'title', 'target', 'rel', 'colspan', 'rowspan']);

const URL_ATTRS = new Set(['href', 'src']);

// style 值里禁止出现的危险片段（命中即删除整个 style 属性）
const DANGEROUS_STYLE = /(expression\s*\(|javascript\s*:|vbscript\s*:|url\s*\(|@import|behaviou?r\s*:|-moz-binding|position\s*:\s*fixed)/i;

// 即便不在白名单也必须整段删除（含子树）的危险元素，集中列出以示意图
const DROP_SUBTREE = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE', 'NOSCRIPT',
  'LINK', 'META', 'BASE', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'OPTION',
  'FRAME', 'FRAMESET', 'APPLET', 'PARAM', 'AUDIO', 'VIDEO', 'SOURCE', 'TRACK'
]);

// 控制字符 + 空白（U+0000..U+0020），用于压缩 URL 后判 scheme，防 "java\tscript:" 绕过
// eslint-disable-next-line no-control-regex
const CONTROL_WS = /[\u0000-\u0020\u00a0]+/g;

function isSafeUrl(value) {
  const raw = String(value === undefined || value === null ? '' : value);
  if (!raw.trim()) return false;
  const compact = raw.replace(CONTROL_WS, '').toLowerCase();
  if (/^(javascript:|vbscript:|data:|blob:|file:)/.test(compact)) return false;
  if (/^(https?:|mailto:|tel:)/.test(compact)) return true;
  // 锚点 / 根路径 / 相对路径
  if (/^[#/.]/.test(raw.trim())) return true;
  // 无 scheme 的相对路径放行；带未知 scheme 的一律拦
  return !/:/.test(raw);
}

function buildConfig(preset) {
  if (preset === 'rich') {
    return { tags: new Set(RICH_TAGS), attrs: new Set(RICH_ATTRS) };
  }
  return { tags: new Set(BASIC_TAGS), attrs: new Set(BASIC_ATTRS) };
}

function scrubAttributes(el, cfg) {
  const attrs = Array.prototype.slice.call(el.attributes || []);
  for (let i = 0; i < attrs.length; i++) {
    const attr = attrs[i];
    const name = attr.name.toLowerCase();
    const value = attr.value;
    // 事件处理器 / 命名空间 / srcset 等一律删
    if (name.indexOf('on') === 0 || name.indexOf('xlink:') === 0 || name === 'srcset' || name === 'formaction' || name === 'xmlns') {
      el.removeAttribute(attr.name);
      continue;
    }
    if (!cfg.attrs.has(name)) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === 'style' && DANGEROUS_STYLE.test(value)) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (URL_ATTRS.has(name) && !isSafeUrl(value)) {
      el.removeAttribute(attr.name);
      continue;
    }
  }
  // 外链新窗口必须 rel=noopener，防 reverse tabnabbing
  if (el.tagName === 'A' && String(el.getAttribute('target') || '').toLowerCase() === '_blank') {
    el.setAttribute('rel', 'noopener noreferrer');
  }
}

function walk(node, cfg) {
  // 先快照子节点，再边遍历边删除，避免实时 NodeList 漏项
  const children = Array.prototype.slice.call(node.childNodes);
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const type = child.nodeType;
    if (type === 1) { // ELEMENT_NODE
      const tag = child.tagName;
      if (DROP_SUBTREE.has(tag) || !cfg.tags.has(tag)) {
        if (child.parentNode) child.parentNode.removeChild(child); // 整段删除，含子树
        continue;
      }
      scrubAttributes(child, cfg);
      walk(child, cfg);
    } else if (type === 8) { // COMMENT_NODE
      if (child.parentNode) child.parentNode.removeChild(child);
    }
    // TEXT_NODE(3) 原样保留：DOMParser 已实体化文本，无法逃逸为标签
  }
}

/**
 * @param {*} dirty 待净化内容（任意类型，会先 String 化）
 * @param {{preset?: 'basic'|'rich'}} [options]
 * @returns {string} 净化后的安全 HTML
 */
export function sanitizeHtml(dirty, options) {
  if (dirty === undefined || dirty === null) return '';
  const input = String(dirty);
  if (!input) return '';

  const ParserCtor = (typeof window !== 'undefined' && window.DOMParser)
    ? window.DOMParser
    : (typeof DOMParser !== 'undefined' ? DOMParser : null);

  // 兜底：无 DOMParser 时退化为纯文本实体转义（绝不输出原始标签）
  if (!ParserCtor) {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  const cfg = buildConfig(options && options.preset);
  const doc = new ParserCtor().parseFromString(input, 'text/html');
  if (!doc || !doc.body) return '';
  walk(doc.body, cfg);
  return doc.body.innerHTML;
}

export default sanitizeHtml;
